import { Button, Flex, Heading, Skeleton, Spacer } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { createApiCredential } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import {
  ApiCredentialFormSchema,
  ApiCredentialFormType,
  emptyApiCredential as emptyApiCredentialForm,
} from "src/schemas/ApiCredential";
import * as breadcrumb from "src/services/breadcrumb";
import { validateWithZod } from "src/services/formValidations";
import { isNonEmptyString } from "src/services/predicates";
import * as Url from "src/services/url";
import * as RD from "src/types/remoteData";
import { ApiCredentialForm } from "./ApiCredentialForm";
import {
  ApiCredentialSecretReveal,
  ApiCredentialSecretRevealProps,
} from "./ApiCredentialSecretReveal";

export const NewApiCredential = () => {
  const toast = useAvelaToast({
    containerStyle: { marginBottom: 16 },
  });
  const [secretReveal, setSecretReveal] =
    useState<ApiCredentialSecretRevealProps | null>(null);
  const organization = useOrganization();
  const organizationId = organization.toNullable()?.id;
  const queryClient = useQueryClient();

  const userGroupsQuery = FSQ.useListUserGroups({}, [], {
    enabled: organization.hasData(),
  });

  const teamsQuery = FSQ.useListTeams(
    organization.hasData()
      ? { query: { organizationId: organization.data.id } }
      : { query: { organizationId: "" } },
    [organizationId],
    { enabled: organization.hasData() }
  );

  const remoteData = React.useMemo(
    () =>
      RD.toTuple(
        RD.fromRestResult(userGroupsQuery),
        RD.fromRestResult(teamsQuery)
      )
        .map(([userGroupsResponse, teamsResponse]) => ({
          userGroups: userGroupsResponse.data.userGroups,
          teams: teamsResponse.data.teams,
        }))
        .mapError(RD.errorResponseToError)
        .mapError(
          (error) =>
            error ??
            new Error(
              "Failed to load API credential options. Please try again."
            )
        ),
    [userGroupsQuery, teamsQuery]
  );

  if (secretReveal) {
    return (
      <Flex direction="column" height="100%" gap={6}>
        <Breadcrumb
          items={breadcrumb.dataServices.getBreadcrumbsForNewApiCredential(
            organization
          )}
          mb={8}
        />
        <ApiCredentialSecretReveal {...secretReveal} />
        <Spacer />
        <AdminFormButtons>
          <Button
            as={NavLink}
            to={organization
              .map((org) => Url.OrgAdmin.DataServices.index(org))
              .withDefault("#")}
            isLoading={organization.isLoading()}
            variant="outline"
            colorScheme="gray"
          >
            Close
          </Button>
        </AdminFormButtons>
      </Flex>
    );
  }

  return (
    <GQLRemoteDataView
      remoteData={remoteData}
      loading={<LoadingSkeleton />}
      reloading={null}
      error={() => (
        <GenericError message="Failed to load API credential options. Please try again." />
      )}
    >
      {({ userGroups, teams }) => {
        const handleSubmit = async (value: ApiCredentialFormType) => {
          try {
            if (!organization.hasData()) throw Error("Invalid organization.");
            const userGroup = userGroups.find(
              (group) => group.id === value.role
            );

            if (!userGroup) {
              throw new Error(`Invalid user group: ${value.role}`);
            }

            const { data: createResult, error } = await createApiCredential({
              body: {
                clientName: value.client_name,
                organizationId: organization.data.id,
                userGroupId: userGroup.id,
                teamId: isNonEmptyString(value.team_id)
                  ? value.team_id
                  : undefined,
                allowedIps: value.allowed_ips,
              },
            });

            if (!createResult) {
              console.warn(
                "Failed to create API credential. Please try again.",
                error
              );
              throw new Error(
                "Failed to create API credential. Please try again."
              );
            }

            toast.closeAll();
            toast({
              id: "create-api-credential",
              title: "API credential created",
            });

            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: [FSQ.useListApiCredentialsKey],
              }),
              queryClient.invalidateQueries({
                queryKey: [FSQ.useGetApiCredentialKey],
              }),
            ]);

            setSecretReveal({
              clientName: value.client_name,
              userGroup: userGroup.label,
              teamName: isNonEmptyString(value.team_id)
                ? teams.find((entry) => entry.id === value.team_id)?.name
                : undefined,
              clientId: createResult.data.clientId,
              clientSecret: createResult.data.clientSecret,
              allowedIps: value.allowed_ips,
            });
          } catch (err) {
            const error = RD.errorResponseToError(
              err && typeof err === "object" && "error" in err
                ? (err as { error: { message: string } })
                : null
            );
            const message =
              error?.message ?? (err instanceof Error ? err.message : "");

            let description = "Check your network and try again.";
            if (message.toLowerCase().includes("conflict")) {
              description = "API credential already exists.";
            } else if (message.toLowerCase().includes("unauthorized")) {
              description = "Not authorized to create API credential.";
            } else {
              console.error(err);
            }

            toast.closeAll();
            toast.error({
              id: "create-api-credential",
              title: `Something went wrong!`,
              description,
              isClosable: true,
            });
          }
        };

        return (
          <Flex direction="column" height="100%">
            <Breadcrumb
              items={breadcrumb.dataServices.getBreadcrumbsForNewApiCredential(
                organization
              )}
              mb={8}
            />

            <Formik<ApiCredentialFormType>
              initialValues={emptyApiCredentialForm}
              validate={validateWithZod(ApiCredentialFormSchema)}
              onSubmit={handleSubmit}
            >
              {(formik) => (
                <Flex
                  as={Form}
                  direction="column"
                  gap={6}
                  flexGrow="1"
                  noValidate
                >
                  <Heading as="h1" variant="admin">
                    Create API credential
                  </Heading>
                  <ApiCredentialForm userGroups={userGroups} teams={teams} />
                  <Spacer />
                  <AdminFormButtons>
                    <Button
                      as={NavLink}
                      to={organization
                        .map((org) => Url.OrgAdmin.DataServices.index(org))
                        .withDefault("#")}
                      isLoading={organization.isLoading()}
                      isDisabled={formik.isSubmitting}
                      variant="outline"
                      colorScheme="gray"
                    >
                      Cancel
                    </Button>
                    <Spacer />
                    <Button type="submit" isLoading={formik.isSubmitting}>
                      Create
                    </Button>
                  </AdminFormButtons>
                </Flex>
              )}
            </Formik>
          </Flex>
        );
      }}
    </GQLRemoteDataView>
  );
};

const LoadingSkeleton = () => {
  return (
    <Flex direction="column" gap={6}>
      <Skeleton height="2rem" width="20rem" />
      <Skeleton height="2.5rem" width="100%" />
      <Skeleton height="2.5rem" width="100%" />
      <Skeleton height="2.5rem" width="50%" />
      <Skeleton height="3rem" width="10rem" alignSelf="flex-end" />
    </Flex>
  );
};
